import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, RefreshCw, Eye, Building } from 'lucide-react'
import apiService from '../services/api'

interface Quote {
  id: string
  projectName?: string
  status: string
  createdAt: string
  projectType?: string
  location?: string
  listing?: {
    title?: string
    vendor?: {
      companyName?: string
    }
  }
  responses?: Array<{
    vendor: {
      id: string
      firstName: string
      lastName: string
      email: string
      companyName: string
    }
  }>
}

export default function BuyerQuoteDashboard() {
  const navigate = useNavigate()
  const [quotes, setQuotes] = useState<Quote[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchQuotes()
  }, [])

  const fetchQuotes = async () => {
    setLoading(true)
    setError(null)
    
    try {
      const response = await apiService.get('/api/enterprise/quotes/buyer')
      console.log('Quotes response:', response)
              // API returns {success: true, data: {quotes: [...quotes...]}}
      setQuotes((response as any).data?.quotes || [])
    } catch (error: any) {
      console.error('Failed to fetch quotes:', error)
      setError(error.response?.data?.message || 'Failed to load quotes')
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return 'bg-yellow-100 text-yellow-800'
      case 'SENT': return 'bg-blue-100 text-blue-800'
      case 'RECEIVED': return 'bg-yellow-100 text-yellow-800'
      case 'RESPONDED': return 'bg-purple-100 text-purple-800'
      case 'ACCEPTED': return 'bg-green-100 text-green-800'
      case 'REJECTED': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          <h1 className="text-3xl font-bold text-gray-900">My Quote Requests</h1>
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading your quotes...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          <h1 className="text-3xl font-bold text-gray-900">My Quote Requests</h1>
          <div className="text-center py-12">
            <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={fetchQuotes}
              className="btn btn-primary"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My Quote Requests</h1>
            <p className="text-gray-600">View and manage all quotes you've sent to enterprise vendors</p>
          </div>
          <button
            onClick={() => navigate('/enterprise')}
            className="btn btn-primary"
          >
            Request New Quote
          </button>
        </div>

        {/* Quote List */}
        {quotes.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm border">
            <Building className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No quote requests yet</h3>
            <p className="text-gray-600 mb-4">Start by requesting quotes from enterprise vendors</p>
            <button
              onClick={() => navigate('/enterprise')}
              className="btn btn-primary"
            >
              Request Your First Quote
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            {/* Table Header */}
            <div className="px-6 py-4 bg-gray-50 border-b">
              <div className="grid grid-cols-5 gap-4 text-sm font-medium text-gray-700">
                <div>Vendor Name</div>
                <div>Quote Title/Reference</div>
                <div>Status</div>
                <div>Date Sent</div>
                <div className="text-right">Actions</div>
              </div>
            </div>

            {/* Quote Rows */}
            <div className="divide-y divide-gray-200">
              {quotes.map((quote) => (
                <div key={quote.id} className="px-6 py-4 hover:bg-gray-50">
                  <div className="grid grid-cols-5 gap-4 items-center">
                    {/* Vendor Name */}
                    <div className="font-medium text-gray-900">
                      {quote.listing?.vendor?.companyName || 
                       quote.responses?.[0]?.vendor?.companyName || 
                       'Enterprise Vendor'}
                    </div>

                    {/* Quote Title/Reference */}
                    <div className="text-gray-900">
                      {quote.projectName || 
                       quote.listing?.title || 
                       `${quote.projectType || 'Quote'} - ${quote.location || 'Location'}`}
                    </div>

                    {/* Status */}
                    <div>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(quote.status)}`}>
                        {quote.status}
                      </span>
                    </div>

                    {/* Date Sent */}
                    <div className="text-gray-600">
                      {formatDate(quote.createdAt)}
                    </div>

                    {/* View Button */}
                    <div className="text-right">
                      <button
                        onClick={() => navigate(`/quotes/${quote.id}`)}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
} 